import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Sparkles,
  Save,
  Send,
  EyeOff,
  Eye as EyeOn,
  Trash2,
  UploadCloud,
  FileText,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useAdminBenchmark,
  useCreateBenchmark,
  useUpdateBenchmark,
  useSuppressBenchmark,
  useUnsuppressBenchmark,
  useDeleteBenchmark,
  useDraftBenchmarkSummary,
  useUploadBenchmarkPdf,
  useDeleteBenchmarkPdf,
  type Benchmark,
} from '@/hooks/useBenchmarkQueries'
import { api } from '@/lib/api'
import { ErrorState } from '@/components/shared/ErrorState'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { RouteStatus } from '@/components/vendor-content/RouteStatus'
import { vendorRoutingUiEnabled } from '@/components/vendor-content/routing-ui-flag'

const PUBLISHER_TYPES = [
  'analyst',
  'vendor',
  'research_firm',
  'consortium',
  'government_agency',
  'other',
] as const

const REPORT_TYPES = [
  'magic_quadrant',
  'wave',
  'marketscape',
  'state_of_cyber',
  'threat_report',
  'cost_study',
  'market_report',
  'whitepaper',
  'benchmark_survey',
  'other',
] as const

interface FormState {
  title: string
  publisher: string
  publisherUrl: string
  publisherType: string
  reportType: string
  topics: string
  reportUrl: string
  keyMetric: string
  description: string
  publishedAt: string
}

const EMPTY_FORM: FormState = {
  title: '',
  publisher: '',
  publisherUrl: '',
  publisherType: 'analyst',
  reportType: 'magic_quadrant',
  topics: '',
  reportUrl: '',
  keyMetric: '',
  description: '',
  publishedAt: '',
}

function toForm(b: Benchmark): FormState {
  return {
    title: b.title,
    publisher: b.publisher,
    publisherUrl: b.publisherUrl ?? '',
    publisherType: b.publisherType,
    reportType: b.reportType,
    topics: b.topics.join(', '),
    reportUrl: b.reportUrl,
    keyMetric: b.keyMetric,
    description: b.description,
    publishedAt: b.publishedAt.slice(0, 10),
  }
}

function formatBytes(size: number | null): string {
  if (!size || size <= 0) return '—'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(2)} MB`
}

export function BenchmarkEditPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id
  const nav = useNavigate()

  const qc = useQueryClient()
  const existing = useAdminBenchmark(id)
  const create = useCreateBenchmark()
  const update = useUpdateBenchmark(id ?? '')
  const suppress = useSuppressBenchmark(id ?? '')
  const unsuppress = useUnsuppressBenchmark(id ?? '')
  const del = useDeleteBenchmark(id ?? '')
  const draft = useDraftBenchmarkSummary()
  const uploadPdf = useUploadBenchmarkPdf(id ?? '')
  const deletePdf = useDeleteBenchmarkPdf(id ?? '')

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)
  const [publishing, setPublishing] = useState(false)

  useEffect(() => {
    if (existing.data) setForm(toForm(existing.data))
  }, [existing.data])

  const canDraftAi = !!form.reportUrl.trim() || !!existing.data?.pdfObjectKey

  const currentStatus = existing.data?.status

  const validate = (): string | null => {
    if (!form.title.trim()) return 'Title is required.'
    if (!form.publisher.trim()) return 'Publisher is required.'
    if (!form.reportUrl.trim()) return 'Report URL is required.'
    if (!form.keyMetric.trim()) return 'Key metric is required.'
    if (!form.description.trim()) return 'Description is required.'
    if (!form.publishedAt) return 'Published date is required.'
    return null
  }

  const buildPayload = () => ({
    title: form.title.trim(),
    publisher: form.publisher.trim(),
    publisherUrl: form.publisherUrl.trim() ? form.publisherUrl.trim() : null,
    publisherType: form.publisherType,
    reportType: form.reportType,
    topics: form.topics
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    reportUrl: form.reportUrl.trim(),
    keyMetric: form.keyMetric.trim(),
    description: form.description,
    publishedAt: new Date(form.publishedAt).toISOString(),
  })

  const handleSave = async (publishAfter = false) => {
    setError(null)
    const v = validate()
    if (v) {
      setError(v)
      return
    }
    try {
      let row: Benchmark
      if (isNew) {
        row = await create.mutateAsync(buildPayload())
      } else {
        row = await update.mutateAsync(buildPayload())
      }
      if (publishAfter) {
        // The publish endpoint is keyed to the returned row's id — this
        // handles both "publish while editing an existing draft" and
        // "create + publish in one click" from the new form, where the
        // URL param id is undefined until after create.
        setPublishing(true)
        try {
          await api.post(`/admin/benchmarks/${row.id}/publish`, {})
          qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
          qc.invalidateQueries({ queryKey: ['benchmarks'] })
        } finally {
          setPublishing(false)
        }
      }
      nav(`/admin/benchmarks/${row.id}/edit`, { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.')
    }
  }

  const handleDraftAi = async () => {
    setError(null)
    try {
      const input: { url?: string; pdfObjectKey?: string } = {}
      if (form.reportUrl.trim()) input.url = form.reportUrl.trim()
      if (existing.data?.pdfObjectKey) input.pdfObjectKey = existing.data.pdfObjectKey
      const out = await draft.mutateAsync(input)
      setForm((f) => ({
        ...f,
        description: out.summary,
        keyMetric: out.keyMetric,
      }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Draft with AI failed.')
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !id) return
    setError(null)
    try {
      await uploadPdf.mutateAsync(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF upload failed.')
    }
  }

  const handleDeletePdf = async () => {
    if (!id) return
    // Simple browser confirm; matches other admin surfaces.
    if (!window.confirm('Detach the current PDF from this benchmark?')) return
    setError(null)
    try {
      await deletePdf.mutateAsync()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete PDF failed.')
    }
  }

  const handleDelete = async () => {
    if (!id) return
    if (!window.confirm('Delete this benchmark? This cannot be undone.')) return
    setError(null)
    try {
      await del.mutateAsync()
      nav('/admin/benchmarks', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.')
    }
  }

  const isSaving = create.isPending || update.isPending || publishing

  const titleHeading = isNew ? 'New benchmark' : 'Edit benchmark'

  const statusBadge = useMemo(() => {
    if (isNew || !currentStatus) return null
    const variantMap = {
      draft: 'outline' as const,
      published: 'default' as const,
      suppressed: 'destructive' as const,
    }
    return (
      <Badge variant={variantMap[currentStatus]} className="capitalize">
        {currentStatus}
      </Badge>
    )
  }, [isNew, currentStatus])

  if (!isNew && existing.isLoading) {
    return (
      <main className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-40 w-full" />
      </main>
    )
  }

  if (!isNew && existing.isError) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <ErrorState
          title="Could not load benchmark"
          message="Retry or go back to the list."
          onRetry={() => existing.refetch()}
        />
      </main>
    )
  }

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-5 p-6">
      <div>
        <Link
          to="/admin/benchmarks"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          Back to benchmarks
        </Link>
      </div>

      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{titleHeading}</h1>
          {statusBadge}
        </div>
        {!isNew && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={del.isPending}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete
          </Button>
        )}
      </header>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* KAN-822 T9 — the Vendor story route for this candidate (dark build flag). */}
      {!isNew && id && vendorRoutingUiEnabled() && <RouteStatus benchmarkId={id} />}

      <section className="grid gap-4">
        <Field
          label="Title"
          value={form.title}
          onChange={(v) => setForm({ ...form, title: v })}
          required
        />
        <Field
          label="Publisher"
          value={form.publisher}
          onChange={(v) => setForm({ ...form, publisher: v })}
          required
        />
        <Field
          label="Publisher URL"
          value={form.publisherUrl}
          onChange={(v) => setForm({ ...form, publisherUrl: v })}
          placeholder="https://example.com"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Publisher type"
            value={form.publisherType}
            options={PUBLISHER_TYPES as unknown as string[]}
            onChange={(v) => setForm({ ...form, publisherType: v })}
          />
          <SelectField
            label="Report type"
            value={form.reportType}
            options={REPORT_TYPES as unknown as string[]}
            onChange={(v) => setForm({ ...form, reportType: v })}
          />
        </div>

        <Field
          label="Topics (comma-separated)"
          value={form.topics}
          onChange={(v) => setForm({ ...form, topics: v })}
          placeholder="endpoint, xdr, ransomware"
        />

        <Field
          label="Report URL"
          value={form.reportUrl}
          onChange={(v) => setForm({ ...form, reportUrl: v })}
          placeholder="https://example.com/report"
          required
        />

        <Field
          label="Key metric"
          value={form.keyMetric}
          onChange={(v) => setForm({ ...form, keyMetric: v })}
          placeholder="6 leaders · 4 visionaries"
          required
        />

        <div className="grid gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="bench-description">Description</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDraftAi}
              disabled={!canDraftAi || draft.isPending}
              className="inline-flex items-center gap-1"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              {draft.isPending ? 'Drafting…' : 'Draft with AI'}
            </Button>
          </div>
          <Textarea
            id="bench-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={6}
            aria-label="description"
            placeholder="Summarise the key findings. AI assist will populate this from the report URL or attached PDF."
          />
        </div>

        <Field
          label="Published at"
          value={form.publishedAt}
          onChange={(v) => setForm({ ...form, publishedAt: v })}
          type="date"
          required
        />
      </section>

      {!isNew && (
        <section className="rounded-lg border p-4">
          <div className="mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
            <h2 className="text-sm font-semibold">Attached PDF</h2>
          </div>
          {existing.data?.pdfObjectKey ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 text-xs text-muted-foreground">
                <p className="truncate font-mono">{existing.data.pdfObjectKey}</p>
                <p className="mt-0.5">
                  {formatBytes(existing.data.pdfSizeBytes)} · {existing.data.pdfContentType ?? 'application/pdf'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted">
                  <UploadCloud className="h-3.5 w-3.5" aria-hidden />
                  Replace
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleUpload}
                    aria-label="pdf file"
                    className="hidden"
                  />
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDeletePdf}
                  disabled={deletePdf.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  Detach
                </Button>
              </div>
            </div>
          ) : (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-4 py-6 text-sm text-muted-foreground hover:bg-muted/30">
              <UploadCloud className="h-4 w-4" aria-hidden />
              {uploadPdf.isPending ? 'Uploading…' : 'Click to upload a PDF (max 25 MB)'}
              <input
                type="file"
                accept="application/pdf"
                onChange={handleUpload}
                aria-label="pdf file"
                className="hidden"
                disabled={uploadPdf.isPending}
              />
            </label>
          )}
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => handleSave(false)} disabled={isSaving}>
          <Save className="h-4 w-4" aria-hidden />
          Save as draft
        </Button>
        {(isNew || currentStatus === 'draft' || currentStatus === 'suppressed') && (
          <Button
            type="button"
            variant="default"
            onClick={() => handleSave(true)}
            disabled={isSaving}
          >
            <Send className="h-4 w-4" aria-hidden />
            Publish
          </Button>
        )}
        {!isNew && currentStatus === 'published' && (
          <Button
            type="button"
            variant="outline"
            onClick={() => suppress.mutateAsync()}
            disabled={suppress.isPending}
          >
            <EyeOff className="h-4 w-4" aria-hidden />
            Suppress
          </Button>
        )}
        {!isNew && currentStatus === 'suppressed' && (
          <Button
            type="button"
            variant="outline"
            onClick={() => unsuppress.mutateAsync()}
            disabled={unsuppress.isPending}
          >
            <EyeOn className="h-4 w-4" aria-hidden />
            Unsuppress
          </Button>
        )}
      </div>
    </main>
  )
}

// ─── Small typed helpers ──────────────────────────────────────────────────────

interface FieldProps {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  required?: boolean
}

function Field({ label, value, onChange, type = 'text', placeholder, required }: FieldProps) {
  const id = `bench-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label.toLowerCase()}
      />
    </div>
  )
}

interface SelectFieldProps {
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
}

function SelectField({ label, value, options, onChange }: SelectFieldProps) {
  const id = `bench-select-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label.toLowerCase()}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm capitalize ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {options.map((o) => (
          <option key={o} value={o} className="capitalize">
            {o.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </div>
  )
}
